import { CustomTable, ErrorBanner } from '@/components';
import Switch from '@/components/Switch';
import { EmailTabs, PATH, TrackingStatus } from '@/constants';
import { getErrorFromAPI, handleToastMutation } from '@/helpers/toast';
import useConfirmationNavigate from '@/hooks/useConfirmationNavigate';
import { useGetNotificationAccountsQuery, useGetUserPlanQuery, useUpdateTemplateMutation } from '@/redux/api/api.caller';
import commonSlice from '@/redux/features/common.slice';
import emailSlice, { emailTabSelector, emailTemplatesSelector } from '@/redux/features/email.slice';
import { Badge, BlockStack, Button, Card, IndexTable, InlineStack, SkeletonBodyText, Text, Tooltip } from '@shopify/polaris';
import { EditIcon, SettingsIcon } from '@shopify/polaris-icons';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { StyledEmailTemplates } from './styled';
import { Receiver } from '@/constants/enum';
import { renderTrackingStatusIcon } from '@/helpers/icon';

const EmailTemplates = () => {
  const navigate = useConfirmationNavigate();
  const dispatch = useDispatch();
  const [apiError, setApiError] = useState<string>('');
  const templatesState = useSelector(emailTemplatesSelector);
  const selectedTab = useSelector(emailTabSelector);
  const [updateTemplate, updatedTemplateData] = useUpdateTemplateMutation();
  const { data, isLoading } = useGetNotificationAccountsQuery();
  const { data: userPlanData } = useGetUserPlanQuery({});

  const emailTemplate = useMemo(() => {
    const handleChangeSwitch = (newValue: boolean, key: TrackingStatus) => {
      dispatch(
        emailSlice.actions.handleChangeEmailTemplates({
          ...templatesState,
          [selectedTab === EmailTabs.ToCustomer ? 'customer' : 'merchant']: {
            ...templatesState[selectedTab === EmailTabs.ToCustomer ? 'customer' : 'merchant'],
            selectedStatus: key,
          },
        }),
      );
      updateTemplate({
        status: key,
        isActive: newValue,
        receiver: selectedTab === EmailTabs.ToCustomer ? Receiver.Customer : Receiver.Merchant,
      }).then((res) => {
        if ('data' in res && res.data && res.data?.state === 1) {
          if (apiError) {
            setApiError('');
          }
          dispatch(commonSlice.actions.handleToast(handleToastMutation(res)));
          return;
        }
        setApiError(getErrorFromAPI(res));
      });
    };

    const navigateToEmailTemplateDetail = (status: TrackingStatus) => {
      // Carry the receiver of the tab being viewed as ?receiver= so the detail loads the
      // right customer/merchant template. Without it the detail fell back to the persisted
      // emailTemplatesTab, which drifts when the in-editor "Send to" dropdown changed it.
      const receiver = selectedTab === EmailTabs.ToCustomer ? Receiver.Customer : Receiver.Merchant;
      dispatch(emailSlice.actions.handleChangeOrderStatus(status));
      navigate(`${PATH.NOTIFICATIONS_EMAIL}/${status}?receiver=${receiver}`);
      window.scrollTo(0, 0);
    };

    if (data && data.data && data.data.email) {
      const { customerTemplates, merchantTemplates } = data.data.email;
      const templatesData = selectedTab === EmailTabs.ToCustomer ? customerTemplates : merchantTemplates;

      return Object.entries(TrackingStatus)
        .filter(([_, value]) => value !== TrackingStatus.All)
        .map(([key, value]) => ({
          id: value,
          orderStatus: (
            <InlineStack gap="400">
              {renderTrackingStatusIcon(value)}
              <Text variant="bodyMd" as="p">
                {key}
              </Text>
            </InlineStack>
          ),
          subject: templatesData.find(({ status }) => status === value)?.subject || '',
          status: () => {
            const currentStatus = templatesState[selectedTab === EmailTabs.ToCustomer ? 'customer' : 'merchant'].status.some(
              (status) => status === value,
            );
            return (
              <div className="center">
                <Switch
                  isActive={currentStatus}
                  onClick={() => handleChangeSwitch(!currentStatus, value)}
                  loading={
                    templatesState[selectedTab === EmailTabs.ToCustomer ? 'customer' : 'merchant'].selectedStatus === value &&
                    updatedTemplateData.isLoading
                  }
                  disable={updatedTemplateData.isLoading}
                />
              </div>
            );
          },
          action: (
            <div className="center">
              <Tooltip content="Edit email templates">
                <Button onClick={() => navigateToEmailTemplateDetail(value)} icon={EditIcon} />
              </Tooltip>
            </div>
          ),
        }));
    }
    return [];
  }, [apiError, data, dispatch, navigate, selectedTab, templatesState, updateTemplate, updatedTemplateData.isLoading]);

  const tableData = emailTemplate.map(({ id, orderStatus, subject, status, action }, index) => (
    <IndexTable.Row id={String(id || 0)} key={id} position={index}>
      <IndexTable.Cell>{orderStatus}</IndexTable.Cell>
      <IndexTable.Cell>{subject}</IndexTable.Cell>
      <IndexTable.Cell>{status()}</IndexTable.Cell>
      <IndexTable.Cell>{action}</IndexTable.Cell>
    </IndexTable.Row>
  ));

  const emailInfo = useMemo(() => {
    if (data && data.data && data.data.email) {
      const { account } = data.data.email;
      if (selectedTab === EmailTabs.ToCustomer) {
        return (
          <BlockStack gap="200">
            <InlineStack gap="200">
              <Text variant="headingMd" as="h3">
                Sender email
              </Text>
              {account && !account.useDefaultEmail && (
                <Badge
                  progress={account.isVerified ? 'complete' : 'partiallyComplete'}
                  tone={account.isVerified ? 'success' : 'attention'}
                >
                  {account.isVerified ? 'verified' : 'not verified'}
                </Badge>
              )}
            </InlineStack>

            <Text variant="bodyMd" as="p">
              {account?.email || 'no-reply@trakow.com'}
            </Text>
          </BlockStack>
        );
      }
      return (
        <BlockStack gap="200">
          <Text variant="headingMd" as="h3">
            Recipient email
          </Text>
          <Text variant="bodyMd" as="p">
            {account?.recipientEmail || userPlanData?.data?.crispData?.email || account?.email}
          </Text>
        </BlockStack>
      );
    }
    return <></>;
  }, [data, selectedTab, userPlanData?.data?.crispData?.email]);

  useEffect(() => {
    if (data && data.data && data.data.email) {
      const {
        data: { email },
      } = data;
      dispatch(
        emailSlice.actions.handleChangeEmailTemplates({
          ...templatesState,
          customer: {
            ...templatesState.customer,
            status: email.customerTemplates.filter(({ isActive }) => isActive).map(({ status }) => status),
          },
          merchant: {
            ...templatesState.merchant,
            status: email.merchantTemplates.filter(({ isActive }) => isActive).map(({ status }) => status),
          },
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return (
    <StyledEmailTemplates>
      <BlockStack gap="800">
        <Card>
          {isLoading ? (
            <SkeletonBodyText lines={3} />
          ) : (
            <InlineStack align="space-between">
              {emailInfo}
              <BlockStack>
                <Button
                  icon={SettingsIcon}
                  onClick={() => navigate(selectedTab === EmailTabs.ToCustomer ? PATH.SENDER_EMAIL : PATH.RECIPIENT_EMAIL)}
                >
                  Edit
                </Button>
              </BlockStack>
            </InlineStack>
          )}
        </Card>

        <BlockStack gap="200">
          <Text variant="headingLg" as="h1">
            Manage shipment notifications
          </Text>
          <Text variant="bodySm" as="p">
            Choose which shipment statuses will send notifications to customers and customize the message content.
          </Text>
        </BlockStack>

        <Card>
          <BlockStack gap="400">
            <ErrorBanner isVisible={!!apiError}>{apiError}</ErrorBanner>
            <CustomTable
              tableProps={{
                itemCount: emailTemplate.length,
                headings: [
                  { title: 'Order status' },
                  { title: 'Subject' },
                  { title: 'Status', alignment: 'center' },
                  { title: 'Action', alignment: 'center' },
                ],
                selectable: false,
              }}
              skeletonLines={20}
              isLoading={isLoading}
            >
              {tableData}
            </CustomTable>
          </BlockStack>
        </Card>
      </BlockStack>
    </StyledEmailTemplates>
  );
};

export default EmailTemplates;
